import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { PageHeader, Spinner } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, isBlankFile, lawyerService } from "../../services/lawyerApi";

const MAX_IMAGES = 6;
const MAX_SERVICE_CHARS = 100;
// The API rejects a weekly price whose monthly equivalent (x4) is under $15.
const MIN_MONTHLY = 15;

const fmt = (v) => Number((Number(v) || 0).toFixed(2)).toLocaleString();

// `services` is stored as JSON; older rows can be a string or objects with a title.
const parseServices = (value) => {
  let list = value;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = [list];
    }
  }
  if (!Array.isArray(list)) return [];
  return list.map((s) => (typeof s === "string" ? s : s?.title || "")).map((s) => s.trim()).filter(Boolean);
};

// weekly_price comes back with the platform fee added; the lawyer's own price is
// what they receive plus the fee amount.
const basePrice = (profile) => {
  const base = Number(profile?.lawyer_weekly_price) + Number(profile?.commission_amount || 0);
  return base > 0 ? String(Number(base.toFixed(2))) : "";
};

const LawyerBusinessServices = () => {
  const { profile, refreshProfile } = useOutletContext();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const loadedFor = useRef(null);

  // images: [{ src, file? }] — file is set for new uploads, src is a URL either way.
  const [images, setImages] = useState([]);
  const [services, setServices] = useState([]);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(-1);
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  // Seed the form once the profile is available.
  useEffect(() => {
    if (!profile?.id || loadedFor.current === profile.id) return;
    loadedFor.current = profile.id;
    setImages((profile.images || []).filter((u) => !isBlankFile(u)).map((src) => ({ src })));
    setServices(parseServices(profile.services));
    setPrice(basePrice(profile));
  }, [profile]);

  // Release preview URLs of uploads when leaving the page.
  const imagesRef = useRef(images);
  imagesRef.current = images;
  useEffect(
    () => () => imagesRef.current.forEach((img) => img.file && URL.revokeObjectURL(img.src)),
    []
  );

  if (!profile?.email) {
    return (
      <div className="lwp-page lwp-page-narrow">
        <PageHeader title="Business Services" backTo="/lawyer/profile" />
        <Spinner />
      </div>
    );
  }

  const fee = Number(profile.partner_commission) || 0;
  const numericPrice = Number(price) || 0;
  const perService = numericPrice * (1 - fee / 100);

  const addImages = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    const room = MAX_IMAGES - images.length;
    if (files.length > room) toast.info(`You can add up to ${MAX_IMAGES} images.`);
    setImages((prev) => [...prev, ...files.slice(0, room).map((file) => ({ file, src: URL.createObjectURL(file) }))]);
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const img = prev[index];
      if (img?.file) URL.revokeObjectURL(img.src);
      return prev.filter((_, i) => i !== index);
    });
  };

  const commitDraft = () => {
    const text = draft.trim();
    if (!text) {
      if (editing >= 0) setEditing(-1);
      return;
    }
    if (editing >= 0) {
      setServices((prev) => prev.map((s, i) => (i === editing ? text : s)));
      setEditing(-1);
    } else {
      setServices((prev) => [...prev, text]);
    }
    setDraft("");
  };

  const editService = (index) => {
    setEditing(index);
    setDraft(services[index]);
  };

  const removeService = (index) => {
    setServices((prev) => prev.filter((_, i) => i !== index));
    if (editing === index) {
      setEditing(-1);
      setDraft("");
    } else if (editing > index) {
      setEditing(editing - 1);
    }
  };

  const save = async () => {
    // A service typed but not yet added still counts.
    const pending = draft.trim();
    const list =
      editing >= 0
        ? services.map((s, i) => (i === editing && pending ? pending : s))
        : pending
        ? [...services, pending]
        : services;

    if (!list.length) return toast.error("Add at least one service.");
    if (!numericPrice || numericPrice <= 0) return toast.error("Enter your service price.");
    if (numericPrice * 4 < MIN_MONTHLY) {
      return toast.error(`Minimum price is $${fmt(MIN_MONTHLY / 4)} ($${MIN_MONTHLY} per month).`);
    }

    setSaving(true);
    try {
      const res = await lawyerService.updateBusinessServices({
        images: images.map((img) => img.file || img.src),
        services: list,
        weeklyPrice: numericPrice,
      });
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success("Business services updated");
      await refreshProfile();
      navigate("/lawyer/profile");
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to update business services."));
    } finally {
      setSaving(false);
    }
  };

  const slots = Array.from({ length: MAX_IMAGES }, (_, i) => images[i] || null);

  return (
    <div className="lwp-page lwp-page-narrow lwp-biz">
      <Seo title="Business Services | Legal Platform" noindex />
      <PageHeader title="Update Profile" backTo="/lawyer/profile" />

      <h3 className="lwp-section-title">Add profile images</h3>
      <section className="lwp-card">
        <div className="lwp-biz-images">
          {slots.map((img, i) =>
            img ? (
              <div key={img.src} className="lwp-biz-image">
                <img src={img.src} alt={`Profile ${i + 1}`} />
                <button type="button" className="lwp-biz-remove" onClick={() => removeImage(i)} aria-label="Remove image">
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            ) : (
              // Any empty slot opens the picker; the last one carries the "+" like the app.
              <button
                key={`add-${i}`}
                type="button"
                className="lwp-biz-image add"
                onClick={() => fileRef.current?.click()}
                aria-label="Add images"
              >
                {i === MAX_IMAGES - 1 && <i className="bi bi-plus-lg" />}
              </button>
            )
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={addImages} />
      </section>

      <h3 className="lwp-section-title">Add Services</h3>
      <section className="lwp-card lwp-biz-services">
        {services.length > 0 && (
          <ul className="lwp-biz-service-list">
            {services.map((s, i) => (
              <li key={`${s}-${i}`} className={editing === i ? "editing" : ""}>
                <span className="lwp-biz-tick">
                  <i className="bi bi-check-lg" />
                </span>
                <p>{s}</p>
                <button type="button" className="lwp-biz-round danger" onClick={() => removeService(i)} aria-label="Delete service">
                  <i className="bi bi-trash3" />
                </button>
                <button type="button" className="lwp-biz-round" onClick={() => editService(i)} aria-label="Edit service">
                  <i className="bi bi-pencil" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <label className="lwp-field">
          <input
            className="lwp-input lwp-biz-input"
            value={draft}
            maxLength={MAX_SERVICE_CHARS}
            placeholder="Write text here..."
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitDraft();
              }
            }}
          />
          <small className="lwp-biz-hint">Maximum {MAX_SERVICE_CHARS} characters allowed</small>
        </label>

        <button type="button" className="lwp-btn lwp-btn-outline lwp-btn-block lwp-biz-add" onClick={commitDraft}>
          <i className="bi bi-plus-lg" /> {editing >= 0 ? "Update Service" : "Add More Services"}
        </button>
      </section>

      <h3 className="lwp-section-title">Add Services Price</h3>
      <section className="lwp-card">
        <label className="lwp-biz-price">
          <span>$</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={price}
            placeholder="0"
            onChange={(e) => setPrice(e.target.value)}
            aria-label="Service price"
          />
        </label>
        <small className="lwp-biz-hint">Platform Fees {fmt(fee)}%</small>
      </section>

      <section className="lwp-card lwp-biz-earn">
        <strong>$ {fmt(perService)}/ One Time Service</strong>
        <small className="lwp-biz-hint">You'll receive this amount</small>
      </section>
      <section className="lwp-card lwp-biz-earn">
        <strong>$ {fmt(perService * 4)}/ Monthly</strong>
        <small className="lwp-biz-hint">You'll receive this amount</small>
      </section>

      <button type="button" className="lwp-btn lwp-biz-submit" onClick={save} disabled={saving}>
        {saving ? <span className="spinner-border spinner-border-sm" /> : "Update Profile"}
      </button>
    </div>
  );
};

export default LawyerBusinessServices;
